import { isDefined } from "@clipboard-health/util-ts";
import { convertToGeoLocation } from "@src/appV2/Location/utils";
import { useModalState } from "@src/appV2/redesign/system/ui-components";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useEffect } from "react";
import { useHistory } from "react-router-dom";

import { useGetWorkplaceProfile } from "../Workplace/useGetWorkplaceProfile";
import { useGetMostRelevantChartingNoncompliance } from "./api/useGetMostRelevantChartingNoncompliance";
import { ChartingNoncomplianceBottomSheet } from "./ChartingNoncomplianceBottomSheet";
import { ChartingNoncomplianceRectifiedBottomSheet } from "./ChartingNoncomplianceRectifiedBottomSheet";
import { ChartingNoncomplianceReviewBottomSheet } from "./ChartingNoncomplianceReviewBottomSheet";
import { SEEN_CHARTING_NONCOMPLIANCE_ID_STORAGE_KEY } from "./constants";

export function AccountOnHoldCallout() {
  const { userId: workerId } = useDefinedWorker();
  const { chartingNoncompliance, noncomplianceExists, isInReview } =
    useGetMostRelevantChartingNoncompliance({ workerId }, { enabled: isDefined(workerId) });

  const history = useHistory();
  const searchParams = new URLSearchParams(history.location.search);
  const chartingRedirect = isDefined(searchParams.get("chartingRedirect"));
  if (chartingRedirect) {
    searchParams.delete("chartingRedirect");
    history.replace({
      search: searchParams.toString(),
    });
  }

  const workplaceId = chartingNoncompliance?.relationships.workplace.data.id;

  const { data: workplace } = useGetWorkplaceProfile(
    { workplaceId: workplaceId ?? "" },
    { enabled: isDefined(workplaceId) }
  );

  const chartingNoncomplianceModalState = useModalState();
  const chartingNoncomplianceRectifiedModalState = useModalState();
  const chartingNoncomplianceReviewModalState = useModalState();

  const seenChartingNoncomplianceId = sessionStorage.getItem(
    SEEN_CHARTING_NONCOMPLIANCE_ID_STORAGE_KEY
  );
  const hasSeenChartingNoncomplianceId =
    isDefined(seenChartingNoncomplianceId) &&
    seenChartingNoncomplianceId === chartingNoncompliance?.id;

  const workplaceCoordinates = isDefined(workplace?.geoLocation.coordinates)
    ? convertToGeoLocation(workplace.geoLocation.coordinates)
    : undefined;

  // Show the bottom sheet once per app open if the worker has a noncompliance and has
  // not seen the bottom sheet for that noncompliance before and is not in review
  useEffect(() => {
    const shouldShowBottomSheet =
      isDefined(chartingNoncompliance?.id) &&
      ((!isInReview && (chartingRedirect || !hasSeenChartingNoncomplianceId)) ||
        (isInReview && chartingRedirect));
    if (shouldShowBottomSheet) {
      chartingNoncomplianceModalState.openModal();
      sessionStorage.setItem(SEEN_CHARTING_NONCOMPLIANCE_ID_STORAGE_KEY, chartingNoncompliance.id);
    }
  }, [
    chartingNoncomplianceModalState,
    chartingNoncompliance?.id,
    hasSeenChartingNoncomplianceId,
    chartingRedirect,
    isInReview,
  ]);

  if (!isDefined(chartingNoncompliance) || !noncomplianceExists || !isDefined(workplace)) {
    return null;
  }

  const {
    relationships: {
      workplace: {
        data: { id: chartingNoncomplianceWorkplaceId },
      },
    },
  } = chartingNoncompliance;

  return (
    <>
      <ChartingNoncomplianceBottomSheet
        chartingNoncomplianceModalState={chartingNoncomplianceModalState}
        chartingNoncomplianceRectifiedModalState={chartingNoncomplianceRectifiedModalState}
        workerId={workerId}
        chartingNoncompliance={chartingNoncompliance}
      />
      <ChartingNoncomplianceRectifiedBottomSheet
        chartingNoncomplianceRectifiedModalState={chartingNoncomplianceRectifiedModalState}
        chartingNoncomplianceReviewModalState={chartingNoncomplianceReviewModalState}
        workerId={workerId}
        workplaceName={workplace.name}
        workplaceId={chartingNoncomplianceWorkplaceId}
        chartingNoncompliance={chartingNoncompliance}
      />
      <ChartingNoncomplianceReviewBottomSheet
        chartingNoncomplianceReviewModalState={chartingNoncomplianceReviewModalState}
        workerId={workerId}
        workplaceId={chartingNoncomplianceWorkplaceId}
        workplaceName={workplace.name}
        workplaceCoordinates={workplaceCoordinates}
        chartingNoncompliance={chartingNoncompliance}
      />
    </>
  );
}
